/**
 * ShopOrderDetailDialog - Order detail and fulfillment management dialog
 * Extracted from AdminShopTab.js in Phase 4D-7
 * 
 * Displays:
 * - Customer information (name, email)
 * - Order status badge
 * - Shipping address (if applicable)
 * - Line items with quantities and prices
 * - Order totals (subtotal, shipping, discount, total)
 * - Tracking information (if shipped)
 * - Status action buttons (context-dependent)
 * 
 * Status Flow:
 * - pending → processing
 * - processing → shipped (delivery) OR ready_for_collection (collection)
 * - shipped → delivered
 * - ready_for_collection → collected
 */
import React from 'react';
import { Truck, ExternalLink } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { formatCurrency } from '../../../utils/format';

/**
 * Get badge variant based on fulfillment status
 * @param {string} status - Fulfillment status
 * @returns {string} Badge variant
 */
function getStatusBadgeVariant(status) {
  switch (status) {
    case 'delivered':
    case 'collected':
      return 'default';
    case 'shipped':
    case 'ready_for_collection':
      return 'secondary';
    case 'cancelled':
      return 'destructive';
    default:
      return 'outline';
  }
}

const STATUS_LABELS = {
  pending: 'Pending',
  processing: 'Processing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  ready_for_collection: 'Ready for Collection',
  collected: 'Collected',
  cancelled: 'Cancelled',
};

function getStatusLabel(status) {
  return STATUS_LABELS[status] || status;
}

/**
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {Object|null} props.order - Selected order object
 * @param {string} props.order.order_id - Order ID
 * @param {string} props.order.customer_name - Customer name
 * @param {string} props.order.customer_email - Customer email
 * @param {string} props.order.fulfillment_status - Current status
 * @param {string} props.order.shipping_method - 'delivery' or 'collection'
 * @param {Object} props.order.shipping_address - Shipping address object
 * @param {Array} props.order.items - Line items
 * @param {number} props.order.subtotal - Subtotal amount
 * @param {number} props.order.shipping_cost - Shipping cost
 * @param {number} props.order.discount_amount - Discount amount
 * @param {number} props.order.total_amount - Total amount
 * @param {string} props.order.tracking_number - Tracking number (optional)
 * @param {string} props.order.tracking_url - Tracking URL (optional)
 * @param {Function} props.onUpdateStatus - Callback when status action clicked (orderId, status)
 * @param {Function} props.onMarkShipped - Callback when Mark Shipped clicked (opens shipping dialog)
 */
export function ShopOrderDetailDialog({
  open,
  onOpenChange,
  order,
  onUpdateStatus,
  onMarkShipped,
  updating = false
}) {
  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Order Details</DialogTitle>
          <DialogDescription>
            Order #{order.order_id.slice(0, 12)}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Customer and Status */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Customer</p>
              <p className="font-medium">{order.customer_name}</p>
              <p className="text-slate-600">{order.customer_email}</p>
            </div>
            <div>
              <p className="text-slate-500">Status</p>
              <Badge variant={getStatusBadgeVariant(order.fulfillment_status)}>
                {getStatusLabel(order.fulfillment_status)}
              </Badge>
            </div>
          </div>
          
          {/* Shipping Address */}
          {order.shipping_address && (
            <div className="text-sm">
              <p className="text-slate-500 mb-1">Shipping Address</p>
              <p>{order.shipping_address.name}</p>
              <p>{order.shipping_address.line1}</p>
              {order.shipping_address.line2 && <p>{order.shipping_address.line2}</p>}
              <p>{order.shipping_address.city}, {order.shipping_address.postcode}</p>
              <p>{order.shipping_address.country}</p>
            </div>
          )}
          
          {/* Line Items */}
          <div>
            <p className="text-slate-500 text-sm mb-2">Items</p>
            <div className="space-y-2">
              {order.items?.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center bg-slate-50 p-2 rounded">
                  <div>
                    <p className="font-medium">{item.name}</p>
                    <p className="text-xs text-slate-500">Qty: {item.quantity}</p>
                  </div>
                  <p className="font-medium">{formatCurrency(item.price * item.quantity)}</p>
                </div>
              ))}
            </div>
          </div>
          
          {/* Order Totals */}
          <div className="border-t pt-3 space-y-1 text-sm">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>{formatCurrency(order.subtotal)}</span>
            </div>
            {order.shipping_cost > 0 && (
              <div className="flex justify-between">
                <span>Shipping</span>
                <span>{formatCurrency(order.shipping_cost)}</span>
              </div>
            )}
            {order.discount_amount > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Discount</span>
                <span>-{formatCurrency(order.discount_amount)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold pt-2 border-t">
              <span>Total</span>
              <span>{formatCurrency(order.total_amount)}</span>
            </div>
          </div>
          
          {/* Tracking Info Display */}
          {(order.tracking_number || order.tracking_url) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
              <p className="text-sm font-medium text-blue-800 flex items-center gap-2">
                <Truck className="h-4 w-4" />
                Shipping Info
              </p>
              {order.tracking_number && (
                <p className="text-sm text-blue-700 mt-1">
                  Tracking #: <span className="font-mono">{order.tracking_number}</span>
                </p>
              )}
              {order.tracking_url && (
                <a 
                  href={order.tracking_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1"
                >
                  Track Package <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          )}
          
          {/* Status Action Buttons */}
          <div className="flex gap-2 pt-2">
            {order.fulfillment_status === 'pending' && (
              <Button 
                onClick={() => onUpdateStatus(order.order_id, 'processing')}
                size="sm"
                disabled={updating}
              >
                Mark Processing
              </Button>
            )}
            {order.fulfillment_status === 'processing' && order.shipping_method !== 'collection' && (
              <Button 
                onClick={onMarkShipped}
                size="sm"
                disabled={updating}
              >
                <Truck className="h-4 w-4 mr-1" />
                Mark Shipped
              </Button>
            )}
            {order.fulfillment_status === 'processing' && order.shipping_method === 'collection' && (
              <Button 
                onClick={() => onUpdateStatus(order.order_id, 'ready_for_collection')}
                size="sm"
                disabled={updating}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Ready for Collection
              </Button>
            )}
            {order.fulfillment_status === 'shipped' && (
              <Button 
                onClick={() => onUpdateStatus(order.order_id, 'delivered')}
                size="sm"
                disabled={updating}
              >
                Mark Delivered
              </Button>
            )}
            {order.fulfillment_status === 'ready_for_collection' && (
              <Button 
                onClick={() => onUpdateStatus(order.order_id, 'collected')}
                size="sm"
                disabled={updating}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Mark Collected
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ShopOrderDetailDialog;
